import { user_settings } from 'src/stores/account/user';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import type { Device, Selection } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { mobile } from 'src/services/mobile-rework';
import { real_id } from '../interface_helpers';
import { capFirstChar } from '../utilities';

/**
 * App Settings is only available inside the Shelly mobile app (per FigJam).
 * Gates the App settings tab, its route guard and its content in both layouts —
 * web/desktop (incl. browser responsive mode) must not surface it.
 */
export const isShellyMobileApp = (): boolean => Boolean(mobile.isMobile);

export function resetSelection(selected: Selection) {
	selected.id = '';
	selected.type = '';
}

/**
 * Clears opposite selection when moving between device and template steps
 * @param index - The current wizard step index
 * @param selected - The current selection object
 */
export function clearIncompatibleSelection(index: number, selected: Selection) {
	if ((index === 1 && selected.type === 'template') || (index === 2 && selected.type === 'device')) {
		selected = { id: '', type: '' };
	}
}

/**
 * Retrieves the switch settings for a given device
 * @param device - The device to get settings for
 * @returns The switch settings object or undefined if not available.
 *
 * Priority order:
 * 1. device_settings_object (used during inclusion process)
 * 2. interface_settings (used for existing devices)
 */
export function getDeviceSwitchSettings(device: Device) {
	const id = real_id(device?.id);
	const channel = device?.channel;

	if (!id || channel == null) return undefined;

	// use this if you want to open TemplatesModal from inclusion process
	// return (
	// 	get(device_settings_object)?.[id]?.[`switch:${channel}`] ??
	// 	get(interface_settings)?.[id]?.[`switch:${channel}`]
	// );
	return devices_settings.getByRealId(id)?.[`switch:${channel}`];
}

/**
 * Checks if clone device settings feature is enabled in user settings
 * @returns boolean indicating if the feature is enabled
 */
export const isCloneDeviceSettingsEnabled = (): boolean => {
	return get(user_settings)?.experimental_features?.cs?.enabled === 1;
};

/**
 * Checks if a device supports the templates feature
 * @param device - The device to check
 * @returns boolean indicating if the device supports templates
 */
export const isDeviceTemplatesSupported = (device: Device): boolean => {
	if (!isCloneDeviceSettingsEnabled()) {
		return false;
	}

	const switchSettings = getDeviceSwitchSettings(device);
	if (!switchSettings) return false;

	const requiredSettings = clonableSettings();
	const hasAllRequiredSettings = requiredSettings.every((setting) => {
		return setting.key in switchSettings;
	});

	return hasAllRequiredSettings;
};

/**
 * Returns list of device settings that can be cloned/applied to other devices
 * @returns Array of clonable settings with their keys and formatted titles
 */
export const clonableSettings = () => {
	const $format = get(format);
	return [
		{
			key: 'auto_on',
			title: $format('text_template_setting_auto_on_title', {
				default: 'Auto on',
			}),
		},
		{
			key: 'auto_off',
			title: $format('text_template_setting_auto_off_title', {
				default: 'Auto off',
			}),
		},
		{
			key: 'auto_on_delay',
			title: $format('text_template_setting_auto_on_delay_title', {
				default: 'Auto on delay',
			}),
		},
		{
			key: 'auto_off_delay',
			title: $format('text_template_setting_auto_off_delay_title', {
				default: 'Auto off delay',
			}),
		},
		{
			key: 'in_mode',
			title: $format('text_template_setting_in_mode_title', {
				default: 'Set relay type',
			}),
		},
		{
			key: 'initial_state',
			title: $format('text_template_setting_initial_state_title', {
				default: 'Set relay power on default',
			}),
		},
	];
};

/**
 * Finds devices that have all the clonable settings present
 * @param device - The source device to find compatible devices for
 * @param sourceSettings - The settings from the source device
 * @returns Array of devices that are compatible for cloning settings
 */
export const getCompatibleDevices = (device: Device, sourceSettings: DeviceSettingsG2) => {
	const allDevices = Object.values(get(devices));

	const clonableSettingsKeys = clonableSettings();

	return allDevices.filter((d) => {
		// Skip the source device itself
		if (real_id(d.id) === real_id(device.id)) {
			return false;
		}

		const targetSettings = getDeviceSwitchSettings(d);

		if (!targetSettings) {
			return false;
		}

		const allKeysPresent = clonableSettingsKeys.every((setting) => {
			const inSource = setting.key in sourceSettings;
			const inTarget = setting.key in targetSettings;

			return inSource && inTarget;
		});

		return allKeysPresent;
	});
};

/**
 * Filters devices that can apply settings configuration
 * @param devices - Array of devices to filter
 * @returns Array of devices that support settings configuration
 */
export const getDevicesThatCanApplySettings = (devices: Device[]): Device[] => {
	return devices.filter((device) => {
		if (!isDeviceTemplatesSupported(device)) {
			return false;
		}

		const switchSettings = getDeviceSwitchSettings(device);
		if (!switchSettings) {
			return false;
		}

		const requiredSettings = clonableSettings();
		const hasAllRequiredSettings = requiredSettings.every((setting) => {
			return setting.key in switchSettings;
		});

		return hasAllRequiredSettings;
	});
};

export const buttonOptions = () => {
	const $format = get(format);
	return [
		{
			key: '5s',
			title: $format('label_5s', {
				default: '5{label_s}',
				values: { label_s: $format('label_s_countdow', { default: 's' }) },
			}),
			value: 5,
		},
		{
			key: '10s',
			title: $format('label_10s', {
				default: '10{label_s}',
				values: { label_s: $format('label_s_countdow', { default: 's' }) },
			}),
			value: 10,
		},
		{
			key: '30s',
			title: $format('label_30s', {
				default: '30{label_s}',
				values: { label_s: $format('label_s_countdow', { default: 's' }) },
			}),
			value: 30,
		},
		{ key: 'Off', title: capFirstChar($format('off', { default: 'Off' })), value: 0 },
	];
};
